/**
 * JFFD PDP: accessible tabs, ingredient modal, hero review scroll target.
 */
(function () {
  function initTabs(root) {
    if (root.dataset.jffdTabsReady === 'true') return;
    root.dataset.jffdTabsReady = 'true';

    const tabs = root.querySelectorAll('.jffd-pdp-tabs__tab');
    const panels = root.querySelectorAll('.jffd-pdp-tabs__panel');
    if (!tabs.length || !panels.length) return;

    function activate(index) {
      tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
        if (panels[i]) panels[i].hidden = !selected;
      });
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(index));
      tab.addEventListener('keydown', (e) => {
        let next = index;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          next = (index + 1) % tabs.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          next = (index - 1 + tabs.length) % tabs.length;
        } else if (e.key === 'Home') {
          e.preventDefault();
          next = 0;
        } else if (e.key === 'End') {
          e.preventDefault();
          next = tabs.length - 1;
        } else {
          return;
        }
        tabs[next].focus();
        activate(next);
      });
    });
  }

  function initDialogs() {
    document.querySelectorAll('[data-jffd-dialog-open]:not([data-jffd-bound])').forEach((btn) => {
      btn.dataset.jffdBound = 'true';
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-jffd-dialog-open');
        const dlg = id && document.getElementById(id);
        if (dlg && typeof dlg.showModal === 'function') {
          dlg.showModal();
          document.body.style.overflow = 'hidden';
          requestAnimationFrame(() => requestAnimationFrame(() => dlg.classList.add('is-open')));
        }
      });
    });

    document.querySelectorAll('.jffd-real-ingredients__dialog:not([data-jffd-dialog-bound])').forEach((dlg) => {
      dlg.dataset.jffdDialogBound = 'true';

      function closeDrawer() {
        dlg.classList.remove('is-open');
        dlg.classList.add('is-closing');
        setTimeout(() => {
          dlg.classList.remove('is-closing');
          dlg.close();
          document.body.style.overflow = '';
        }, 350);
      }

      dlg.querySelectorAll('[data-jffd-dialog-close]').forEach((el) => {
        el.addEventListener('click', closeDrawer);
      });

      dlg.addEventListener('cancel', (e) => {
        e.preventDefault();
        closeDrawer();
      });

      dlg.addEventListener('click', (e) => {
        if (e.target === dlg) closeDrawer();
      });
    });
  }

  function initHeroReviewScroll() {
    document.querySelectorAll('[data-jffd-scroll-to-reviews]:not([data-jffd-bound])').forEach((btn) => {
      btn.dataset.jffdBound = 'true';
      btn.addEventListener('click', (e) => {
        const id = btn.getAttribute('data-jffd-scroll-to-reviews');
        const target = id && document.getElementById(id);
        if (target) {
          e.preventDefault();

          const bodyStyles = window.getComputedStyle(document.body);
          const headerGroupHeight = Number.parseFloat(bodyStyles.getPropertyValue('--header-group-height')) || 0;
          const headerHeight = Number.parseFloat(bodyStyles.getPropertyValue('--header-height')) || 0;
          const stickyOffset = Math.max(headerGroupHeight, headerHeight);
          const targetTop = target.getBoundingClientRect().top + window.scrollY;

          window.scrollTo({
            top: Math.max(targetTop - stickyOffset, 0),
            behavior: 'smooth',
          });
          target.focus({ preventScroll: true });
        }
      });
    });
  }

  function initIngredientSlideClick() {
    document.querySelectorAll('.jffd-real-ingredients__slide:not([data-jffd-bound])').forEach((slide) => {
      const section = slide.closest('.jffd-pdp-real-ingredients');
      if (!section) return;
      const btn = section.querySelector('[data-jffd-dialog-open]');
      if (!btn) return;
      slide.dataset.jffdBound = 'true';
      slide.setAttribute('role', 'button');
      slide.addEventListener('click', () => btn.click());
      slide.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          btn.click();
        }
      });
    });
  }

  function initCompareBoxDrawer() {
    document.querySelectorAll('[data-jffd-compare-box-open]:not([data-jffd-bound])').forEach((btn) => {
      btn.dataset.jffdBound = 'true';
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-jffd-compare-box-open');
        const dlg = id && document.getElementById(id);
        if (dlg && typeof dlg.showModal === 'function') {
          dlg.showModal();
          document.body.style.overflow = 'hidden';
          requestAnimationFrame(() => requestAnimationFrame(() => dlg.classList.add('is-open')));
        }
      });
    });

    document.querySelectorAll('.jffd-compare-box-drawer:not([data-jffd-cbox-bound])').forEach((dlg) => {
      dlg.dataset.jffdCboxBound = 'true';

      function closeDrawer() {
        dlg.classList.remove('is-open');
        dlg.classList.add('is-closing');
        setTimeout(() => {
          dlg.classList.remove('is-closing');
          dlg.close();
          document.body.style.overflow = '';
        }, 350);
      }

      dlg.querySelectorAll('[data-jffd-compare-box-close]').forEach((el) => {
        el.addEventListener('click', closeDrawer);
      });

      dlg.addEventListener('cancel', (e) => {
        e.preventDefault();
        closeDrawer();
      });

      dlg.addEventListener('click', (e) => {
        if (e.target === dlg) closeDrawer();
      });
    });
  }

  function initSellingPlanToggle() {
    // Enable/disable the frequency select based on which purchase option
    // (ship_once vs subscription) is currently checked. Re-run from initAll()
    // on every section load so a replaced fieldset (variant morph, section
    // re-render) gets fresh element references instead of stale ones.
    const fieldset = document.querySelector(
      '.jffd-selling-plan-selector:not([data-line-index]):not([data-jffd-plan-toggle-bound])'
    );
    if (!fieldset) return;
    fieldset.dataset.jffdPlanToggleBound = 'true';

    const sellingPlanSelect = fieldset.querySelector('[name="selling_plan"]');
    const shipOnceRadio = fieldset.querySelector('input[value="ship_once"]');
    const subscribeRadio = fieldset.querySelector('input[value="subscription"]');

    function toggleSellingPlan() {
      if (!(sellingPlanSelect instanceof HTMLSelectElement)) return;
      const shipOnceSelected = shipOnceRadio instanceof HTMLInputElement && shipOnceRadio.checked;

      if (shipOnceSelected) {
        sellingPlanSelect.style.display = 'none';
        sellingPlanSelect.disabled = true;
      } else {
        sellingPlanSelect.style.display = '';
        sellingPlanSelect.disabled = false;
      }
    }

    if (shipOnceRadio) shipOnceRadio.addEventListener('change', toggleSellingPlan);
    if (subscribeRadio) subscribeRadio.addEventListener('change', toggleSellingPlan);

    toggleSellingPlan();
  }

  function initAll() {
    document.querySelectorAll('jffd-pdp-tabs').forEach(initTabs);
    initDialogs();
    initCompareBoxDrawer();
    initHeroReviewScroll();
    initIngredientSlideClick();
    initSellingPlanToggle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();

function bindChangeListener(element, ancestorSelector, handler) {
  const ancestor = element.closest(ancestorSelector);
  ancestor?.addEventListener('change', handler);
  return ancestor;
}

function unbindChangeListener(element, ancestorSelector, handler) {
  element.closest(ancestorSelector)?.removeEventListener('change', handler);
}

function setVisible(el, visible) {
  if (visible) el.removeAttribute('hidden');
  else el.setAttribute('hidden', '');
}

class JffdPurchaseOptionPrice extends HTMLElement {
  connectedCallback() {
    bindChangeListener(this, '.shopify-section, dialog', this.#onFormChange);
  }

  disconnectedCallback() {
    unbindChangeListener(this, '.shopify-section, dialog', this.#onFormChange);
  }

  /** @param {Event} event */
  #onFormChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    if (!target.name.startsWith('purchase_option_')) return;
    this.update(target.value);
  };

  /** @param {string} purchaseOption */
  update(purchaseOption) {
    this.querySelectorAll('[data-purchase-option-price]').forEach((section) => {
      setVisible(section, section.getAttribute('data-purchase-option-price') === purchaseOption);
    });
  }
}

if (!customElements.get('jffd-purchase-option-price')) {
  customElements.define('jffd-purchase-option-price', JffdPurchaseOptionPrice);
}

/**
 * Mirrors Ordergroove's own Subscribe & Save / Ship Once toggle onto the
 * theme's main price block. jffd-selling-plan-selector.liquid's radios
 * already trigger JffdPurchaseOptionPrice via a native 'change' event; OG's
 * <og-optin-button>/<og-optout-button> are OG's own custom elements with no
 * equivalent, so a delegated click listener calls the same public
 * `update()` method directly, scoped to whichever section was clicked in.
 */
// Mirror clicks on Ordergroove opt-in/out buttons to the theme price components
document.querySelectorAll('.og-subscribtion-block-pdp').forEach((ogBlock) => {
  ogBlock.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const subscriptionBlock = event.target?.closest('.og-subscription-block');
      if (!subscriptionBlock || !ogBlock.contains(subscriptionBlock)) return;

      const optinButton = event.target?.closest('.og-subscription-block og-optin-button');
      const optoutButton = event.target?.closest('.og-subscription-block og-optout-button');
      if (!optinButton && !optoutButton) return;

      const purchaseOption = optinButton ? 'subscription' : 'ship_once';
      const section = ogBlock?.closest('.shopify-section, dialog');
      if (!section) return;

      section.querySelectorAll('jffd-purchase-option-price').forEach((el) => {
        if (typeof el?.update === 'function') {
          el.update(purchaseOption);
        }
      });
      section.querySelectorAll('pet-feeding-details').forEach((el) => {
        if (typeof el.updateEverydayPrice === 'function') {
          el.updateEverydayPrice();
        }
      });
    },
    { passive: true }
  );
});

class JffdOgPricingBridge extends HTMLElement {
  connectedCallback() {
    const variantPrice = Number(this.dataset.variantPrice) || 0;
    const discountPercent = Number(this.dataset.discountPercent) || 0;
    const thresholdCents = Number(this.dataset.thresholdCents) || 0;

    // Truncate rather than round, matching price.liquid's subscription_sale_price
    // calculation for the same variant — otherwise this widget and the theme's
    // own price display can disagree by a cent for the same product.
    let discountAmount = Math.floor((variantPrice * discountPercent) / 100);
    if (thresholdCents > 0 && discountAmount > thresholdCents) {
      discountAmount = thresholdCents;
    }
    const subscriptionPrice = variantPrice - discountAmount;

    const regularText = `$${(variantPrice / 100).toFixed(2)}`;
    const subscriptionText = `$${(subscriptionPrice / 100).toFixed(2)}`;

    const scope = this.closest('.shopify-section, dialog') || document;

    scope.querySelectorAll('og-offer .jffd-og-price[data-price-type="regular"]').forEach((el) => {
      el.textContent = regularText;
    });
    scope.querySelectorAll('og-offer .jffd-og-price[data-price-type="subscription"]').forEach((el) => {
      el.textContent = subscriptionText;
    });
  }
}

if (!customElements.get('jffd-og-pricing-bridge')) {
  customElements.define('jffd-og-pricing-bridge', JffdOgPricingBridge);
}

/**
 * Ordergroove's offer template ships both versions of its first benefit line:
 * `.og-benefit-1--guest` ("50% off 1st order and 5% off future orders") and
 * `.og-benefit-1--claimed` ("5% off recurring orders"). Which one applies
 * depends on whether the customer has already used the first-order incentive -
 * something only Liquid can tell (settings.og_initial_discount_used_tag against
 * customer.tags), which is the same check jffd-selling-plan-selector.liquid
 * makes for the theme's own plan selector. theme.liquid publishes the result as
 * window.JFFD_OG_INITIAL_DISCOUNT_USED; this shows the matching line.
 *
 * OG injects its offer markup asynchronously and re-renders it as the shopper
 * toggles subscribe/ship-once, so the toggle re-runs on DOM changes instead of
 * only once on load.
 */
(() => {
  const GUEST_BENEFIT = '.og-benefit-1--guest';
  const CLAIMED_BENEFIT = '.og-benefit-1--claimed';
  const KNOWN_BENEFIT_1_CLASSES = ['og-benefit-1--guest', 'og-benefit-1--claimed'];

  /**
   * @param {HTMLElement} element
   * @param {boolean} visible
   */
  function setDisplay(element, visible) {
    const display = visible ? '' : 'none';
    if (element.style.display !== display) element.style.display = display;
  }

  // Keeps OG's own checkmark <svg> instead of wiping it out with the new text.
  function setBenefitText(element, html) {
    if (!(element instanceof HTMLElement) || !html || element.dataset.jffdTextApplied === 'true') return;

    const icon = element.querySelector('svg');
    const textWrapper = document.createElement('span');
    textWrapper.innerHTML = html;

    element.textContent = '';
    if (icon) element.appendChild(icon);
    element.appendChild(textWrapper);
    element.dataset.jffdTextApplied = 'true';
  }

  function applyBenefitVisibility() {
    const hasClaimedIncentive = window.JFFD_OG_INITIAL_DISCOUNT_USED === true;

    document.querySelectorAll(GUEST_BENEFIT).forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      setDisplay(el, !hasClaimedIncentive);
      setBenefitText(el, window.JFFD_OG_BENEFIT_1_GUEST);
    });
    document.querySelectorAll(CLAIMED_BENEFIT).forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      setDisplay(el, hasClaimedIncentive);
      setBenefitText(el, window.JFFD_OG_BENEFIT_1_CLAIMED);
    });

    const benefit2Html = window.JFFD_OG_BENEFIT_2;
    const benefit3Html = window.JFFD_OG_BENEFIT_3;
    if (!benefit2Html && !benefit3Html) return;

    document.querySelectorAll('.og-offer-benefits').forEach((list) => {
      const otherLines = Array.from(list.children).filter(
        (li) => !KNOWN_BENEFIT_1_CLASSES.some((cls) => li.classList.contains(cls))
      );

      const [line2, line3] = otherLines;
      setBenefitText(line2, benefit2Html);
      setBenefitText(line3, benefit3Html);
    });
  }

  let scheduled = false;
  function scheduleBenefitVisibility() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      applyBenefitVisibility();
    });
  }

  function startBenefitVisibility() {
    applyBenefitVisibility();
    // Only childList is observed, so the inline `display` writes above can't
    // retrigger the observer.
    new MutationObserver(scheduleBenefitVisibility).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.body) startBenefitVisibility();
  else document.addEventListener('DOMContentLoaded', startBenefitVisibility);
})();
